import { Box, Divider, Typography } from '@mui/material';
import CustomNotes from 'components/shared/CustomNotes';
import { CompleteOperationType, Operation, StartOperationType } from 'model/alerts.model';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import Section from 'components/shared/Section';
import { useEffect } from 'react';
import {
  fetchPriceIntelligenceAndServices,
  Service,
} from 'store/slices/pricingIntelligenceAndServicesSlice';
import zeplinColor from 'theme/Color';
import { useDealNotes } from '../hooks/useDealNotes';
import DetailSection from './DetailSection';
import { muiStyles } from './muiStyles';
import Sidebar from './Sidebar';
import { generateSectionData, mapDealToDetailFields } from './utils';

const Details = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const deal = useAppSelector((state) => state.deals.deal);
  const { services } = useAppSelector((state) => state.pricingIntelligenceAndServices);

  const startOperation: StartOperationType = (operation: Operation, message: string) => {};
  const completeOperation: CompleteOperationType = (
    operation: Operation,
    message: string,
    isError?: boolean,
  ) => {};

  useEffect(() => {
    if (id) dispatch(fetchPriceIntelligenceAndServices(id));
  }, [id, dispatch]);

  const manager = useDealNotes(id!, startOperation, completeOperation);
  const map = mapDealToDetailFields(deal, deal?.dealType?.dealTypeKey, services);
  const sections = generateSectionData(deal?.dealType?.dealTypeKey, map);

  return (
    <Box sx={muiStyles.root}>
      <Box sx={muiStyles.sections}>
        {sections
          .filter(({ data }) => data !== null)
          .map(({ label, data }) => {
            if (label === 'Service Information') {
              return (
                <Section key={label} title={label}>
                  {data.map((service: Service) => (
                    <>
                      <Box key={service.serviceName} sx={{ display: 'flex', flex: 1 }}>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <Typography variant='responsiveHeading5'>
                            {service.serviceName}
                          </Typography>
                          <Typography
                            variant='responsiveBody3'
                            sx={{ color: zeplinColor.Neutral8 }}
                          >
                            Service Name
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <Typography variant='responsiveHeading5'>{service.floorRate}</Typography>
                          <Typography
                            variant='responsiveBody3'
                            sx={{ color: zeplinColor.Neutral8 }}
                          >
                            Floor Price
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <Typography variant='responsiveHeading5'>
                            {service.targetPrice}
                          </Typography>
                          <Typography
                            variant='responsiveBody3'
                            sx={{ color: zeplinColor.Neutral8 }}
                          >
                            Target Price
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <Typography variant='responsiveHeading5'>
                            {service.ceilingRate}
                          </Typography>
                          <Typography
                            variant='responsiveBody3'
                            sx={{ color: zeplinColor.Neutral8 }}
                          >
                            Ceiling Price
                          </Typography>
                        </Box>
                      </Box>
                      <Divider
                        sx={{
                          margin: '10px 0',
                          borderWidth: '1px',
                          borderColor: zeplinColor.Neutral5,
                          width: '100%',
                          alignSelf: 'center',
                          ':last-child': {
                            display: 'none',
                          },
                        }}
                      />
                    </>
                  ))}
                </Section>
              );
            }
            return <DetailSection key={label} label={label} data={data} />;
          })}

        <Box sx={{ height: '100%' }}>
          <CustomNotes manager={manager} />
        </Box>
      </Box>

      <Sidebar />
    </Box>
  );
};

export default Details;
